import { create } from 'zustand';
import { feedbackApi } from '../services/api';
import { getSessionStatusFilterValue } from '../constants/feedbackSessionStatuses';

// Central initial state for easy reset
const initialState = {
  sessions: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  // Filters
  searchTerm: '',
  selectedStatuses: [],      // Array of status codes (e.g., ['NOT_STARTED', 'IN_PROGRESS'])
  selectedReviewTypes: [],   // Array of review type texts (e.g., ['Sequential', 'Parallel'])
  selectedYear: '',          // Filter by session year (e.g., '2025')
  selectedBusinessUnitId: '', // Filter by business unit ID
  // UI State
  isReturningFromDetails: false,
  isRefreshing: false,       // ADR-192 §11: silent background refresh in flight
  showControls: true,
};

const useFeedbackSessionsStore = create((set, get) => {
  // Since the API returns all sessions at once, we filter client-side
  const fetchSessions = async () => {
    if (get().isLoading) {
      return null;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await feedbackApi.getSessions();

      if (response) {
        set({
          sessions: response,
          pageInfo: {
            endCursor: null,
            hasNextPage: false,
            totalCount: response.length
          },
          isLoading: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false });
      return null;
    } catch (err) {
      set({
        error: err.message || 'Failed to load feedback sessions',
        isLoading: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // UI State Actions
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setShowControls: (show) => set({ showControls: show }),

    // Filter setters
    setSearchTerm: (searchTerm) => {
      set({ searchTerm });
    },

    setSelectedStatuses: (statuses) => {
      set({ selectedStatuses: statuses });
    },

    setSelectedReviewTypes: (reviewTypes) => {
      set({ selectedReviewTypes: reviewTypes });
    },

    setSelectedYear: (year) => {
      set({ selectedYear: year });
    },

    setSelectedBusinessUnitId: (businessUnitId) => {
      set({ selectedBusinessUnitId: businessUnitId });
    },

    // Toggle handlers for chip-based filters
    toggleStatus: (status) => {
      const { selectedStatuses } = get();
      if (selectedStatuses.includes(status)) {
        set({ selectedStatuses: selectedStatuses.filter(s => s !== status) });
      } else {
        set({ selectedStatuses: [...selectedStatuses, status] });
      }
    },

    toggleReviewType: (reviewType) => {
      const { selectedReviewTypes } = get();
      if (selectedReviewTypes.includes(reviewType)) {
        set({ selectedReviewTypes: selectedReviewTypes.filter(rt => rt !== reviewType) });
      } else {
        set({ selectedReviewTypes: [...selectedReviewTypes, reviewType] });
      }
    },

    // Data actions
    fetchSessionsWithReset: () => fetchSessions(),

    // ADR-192 §11: background refresh used when returning from a session's
    // detail pages. Changes made there (removing every assignee, adding one,
    // publishing) change server-computed fields such as `canDelete` and the
    // counts on the card. Unlike fetchSessions it never toggles isLoading, so
    // the grid is not swapped for a loader and the restored scroll position
    // holds. Errors are swallowed: the list already shows the previous data.
    refreshSessionsSilently: async () => {
      if (get().isLoading || get().isRefreshing) {
        return null;
      }
      set({ isRefreshing: true });
      try {
        const response = await feedbackApi.getSessions();
        if (response) {
          set({
            sessions: response,
            pageInfo: {
              endCursor: null,
              hasNextPage: false,
              totalCount: response.length
            }
          });
        }
        return response || null;
      } catch {
        return null;
      } finally {
        set({ isRefreshing: false });
      }
    },

    // No-op since API doesn't support pagination yet
    fetchSessionsLoadMore: () => Promise.resolve(null),

    // Update a session in the store (after edit)
    updateSession: (updatedSession) => {
      if (!updatedSession || !updatedSession.id) {
        return false;
      }
      const { sessions, pageInfo } = get();
      const index = sessions.findIndex(session => session.id === updatedSession.id);

      if (index !== -1) {
        const updatedSessions = [...sessions];
        updatedSessions[index] = { ...updatedSessions[index], ...updatedSession };
        set({ sessions: updatedSessions });
        return true;
      } else {
        // Add new session
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: pageInfo.totalCount + 1
        };
        set({
          sessions: [updatedSession, ...sessions],
          pageInfo: updatedPageInfo
        });
        return true;
      }
    },

    // Remove a session from the store
    removeSession: (sessionId) => {
      if (!sessionId) {
        return false;
      }

      const { sessions, pageInfo } = get();
      const index = sessions.findIndex(session => session.id === sessionId);

      if (index !== -1) {
        const updatedSessions = sessions.filter(session => session.id !== sessionId);
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: Math.max(0, pageInfo.totalCount - 1)
        };
        set({
          sessions: updatedSessions,
          pageInfo: updatedPageInfo
        });
        return true;
      }
      return false;
    },

    // Filters reset
    resetFilters: () => {
      set({
        searchTerm: '',
        selectedStatuses: [],
        selectedReviewTypes: [],
        selectedYear: '',
        selectedBusinessUnitId: '',
      });
    },

    // State reset
    resetState: () => set({
      ...initialState,
    }),

    // Computed: get filtered sessions
    getFilteredSessions: () => {
      const { sessions, searchTerm, selectedStatuses, selectedReviewTypes, selectedYear, selectedBusinessUnitId } = get();

      return sessions.filter(session => {
        // Search filter
        const matchesSearch = !searchTerm ||
          session.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          session.businessUnit?.name?.toLowerCase().includes(searchTerm.toLowerCase());

        // Status filter (computed from session date fields).
        // ADR-014 §10: use the display/filter vocabulary so a session closed
        // early matches the "Closed" chip, not "Completed".
        const sessionStatus = getSessionStatusFilterValue(session);
        const matchesStatus = selectedStatuses.length === 0 ||
          selectedStatuses.includes(sessionStatus);

        // Review type filter (using the text representation)
        const matchesReviewType = selectedReviewTypes.length === 0 ||
          selectedReviewTypes.includes(session.reviewType);

        // Year filter
        const matchesYear = !selectedYear ||
          String(session.year) === String(selectedYear);

        // Business unit filter
        const matchesBusinessUnit = !selectedBusinessUnitId ||
          session.businessUnitId === selectedBusinessUnitId;

        return matchesSearch && matchesStatus && matchesReviewType && matchesYear && matchesBusinessUnit;
      });
    },
  };
});

export default useFeedbackSessionsStore;
